/**
 * The built-in **terrain tile catalogue** (`decisions/0184`, `decisions/0185`).
 *
 * A GM paints named tiles ("thick brush", "boulders", "lava field"), and a painted cell stores
 * only the tile's id. The tile is the source of the cell's mechanics: every engine reader resolves
 * a cell's {@link TerrainProperties} through {@link tileProperties} (via `terrain.ts`'s
 * `cellProperties`), so editing a catalogue entry here changes every map that uses it, live and
 * stored. **A catalogue edit is a rules change: name it in the commit.**
 *
 * **Ids are durable.** A stored map names tiles by id. Renaming a display `name` is free; removing
 * or renaming an id breaks stored maps loudly (the reducer and the load wall both reject an
 * unknown id), so it needs a deliberate data change.
 *
 * A one-off cell is the `"custom"` tile, which carries its own properties and is its own source:
 * editing the catalogue never changes it.
 *
 * The catalogue is a code constant, not pack content, because the reducer and the engine are pure
 * and must resolve a tile without world IO (`decisions/0185` point 3). Pack-authored tiles are
 * parked until the pack format settles.
 *
 * Rock outcrop and giant trunk carry `cover: "hard"`, as the solid cells they replaced did (mapgen's
 * solid core; prod landing-zone). The engine does read it: `visibility`'s hard-cover adjacency gate
 * (`packages/space/src/los.ts`) counts a sight-blocking neighbour's hard cover, so a target next to
 * one takes hard cover from a line through distant hard cover. Whether an impassable tile should
 * give cover is parked (`projects/terrain-tile-types.md` ruling 5); until it is ruled, these tiles
 * keep the pre-v20 outcome. Bulkhead, rock wall and chasm had no pre-v20 counterpart and carry no cover.
 */

import type { TerrainCell, TerrainProperties } from "./terrain";

/** One catalogue entry: the display name, its mechanical properties, and a one-line description. */
export type TileType = {
  /** Display name — free to change; the id is what stored maps reference. */
  name: string;
  /** The mechanics a cell painted with this tile has. Already normalized (the pin test checks). */
  properties: TerrainProperties;
  /** A short plain-words description of what the tile is. */
  blurb: string;
};

const DIFFICULT = 2;

/**
 * The catalogue. Keys are the durable tile ids. `open-ground` has no properties, and painting it
 * clears the cell, so it is never stored.
 */
export const TERRAIN_TILES = {
  "open-ground": { name: "Open ground", properties: {}, blurb: "Clear, level ground." },
  scrub: { name: "Scrub", properties: { cover: "soft" }, blurb: "Low, sparse vegetation that breaks up a sightline." },
  "scattered-rocks": { name: "Scattered rocks", properties: { cover: "soft" }, blurb: "Loose rocks and small stones, easy to step over." },
  "dead-growth": { name: "Dead growth", properties: { cover: "soft" }, blurb: "Standing dead vegetation, brittle and chest-high on a mech: it hides you and crumbles as you push through." },
  "thick-brush": { name: "Thick brush", properties: { moveCost: DIFFICULT, cover: "soft" }, blurb: "Dense undergrowth: slow going, and it hides you." },
  scree: { name: "Scree", properties: { moveCost: DIFFICULT }, blurb: "Loose, sliding rubble." },
  mud: { name: "Mud", properties: { moveCost: DIFFICULT }, blurb: "Deep, sucking mud." },
  "oil-slick": { name: "Oil slick", properties: { moveCost: DIFFICULT }, blurb: "Slippery spilled fuel or lubricant." },
  "tangled-roots": { name: "Tangled roots", properties: { moveCost: DIFFICULT }, blurb: "Knotted surface roots at the foot of a big trunk, catching every step." },
  boulders: { name: "Boulders", properties: { cover: "hard" }, blurb: "Large rocks, solid enough to hide behind." },
  trees: { name: "Trees", properties: { cover: "hard" }, blurb: "Standing trunks thick enough to hide a mech; you move between them." },
  wreckage: { name: "Wreckage", properties: { moveCost: DIFFICULT, cover: "hard" }, blurb: "Twisted metal and debris (Core p.81)." },
  deadfall: { name: "Deadfall", properties: { moveCost: DIFFICULT, cover: "hard" }, blurb: "Fallen trunks and tangled branches: slow to climb over, solid to hide behind." },
  "rock-outcrop": { name: "Rock outcrop", properties: { blocksMovement: true, blocksSight: true, cover: "hard" }, blurb: "A rock formation too tall to climb or see past." },
  "giant-trunk": { name: "Giant trunk", properties: { blocksMovement: true, blocksSight: true, cover: "hard" }, blurb: "A trunk as wide as a building, too tall to climb or see past." },
  bulkhead: { name: "Bulkhead", properties: { blocksMovement: true, blocksSight: true, blocksFlight: true }, blurb: "A floor-to-ceiling wall: nothing passes, not even a flyer." },
  "rock-wall": { name: "Rock wall", properties: { blocksMovement: true, blocksSight: true, blocksFlight: true }, blurb: "A sheer cliff face or cavern wall: nothing climbs it or flies over it." },
  chasm: { name: "Chasm", properties: { blocksMovement: true }, blurb: "A drop ground movers cannot cross; flyers can, and you can see across it." },
  crates: { name: "Crates", properties: { cover: "soft" }, blurb: "Stacked supply crates, waist-high on a mech." },
  "cargo-stacks": { name: "Cargo stacks", properties: { cover: "hard" }, blurb: "Shipping containers stacked high enough to hide a mech." },
  "cable-snarl": { name: "Cable snarl", properties: { moveCost: DIFFICULT, cover: "soft" }, blurb: "Coiled cable and loose spools across the deck: slow going, and it hides you." },
  "loose-debris": { name: "Loose debris", properties: { moveCost: DIFFICULT }, blurb: "Scattered metal fragments underfoot." },
  "lava-field": { name: "Lava field", properties: { moveCost: DIFFICULT, dangerous: true, hazard: "burn" }, blurb: "Cooling lava crust over molten rock." },
  "acid-pool": { name: "Acid pool", properties: { dangerous: true, hazard: "energy" }, blurb: "A pool of corrosive liquid." },
  minefield: { name: "Minefield", properties: { dangerous: true, hazard: "explosive" }, blurb: "Buried explosives." },
} satisfies Record<string, TileType>;

/** A catalogue tile id. */
export type TileTypeId = keyof typeof TERRAIN_TILES;

/** The id a stored one-off cell uses: it carries its own properties. */
export const CUSTOM_TILE = "custom";

/** The tile whose paint clears a cell. */
export const OPEN_GROUND_TILE: TileTypeId = "open-ground";

/** Every catalogue id, in catalogue order. */
export const TILE_TYPE_IDS = Object.keys(TERRAIN_TILES) as TileTypeId[];

/** Whether `id` names a catalogue tile (not `"custom"`). */
export function isTileTypeId(id: unknown): id is TileTypeId {
  return typeof id === "string" && Object.prototype.hasOwnProperty.call(TERRAIN_TILES, id);
}

/** The catalogue entry for `id`, or `undefined` for an unknown id. */
export function tileType(id: string): TileType | undefined {
  return isTileTypeId(id) ? (TERRAIN_TILES as Record<TileTypeId, TileType>)[id] : undefined;
}

/**
 * The mechanical properties of a stored cell: a custom cell's own properties, or its catalogue
 * tile's. **Read at call time** — nothing caches or stamps a tile's properties into a cell, which
 * is what makes the tile the source (`decisions/0185`). An unknown id resolves to no properties;
 * the reducer and the load wall keep unknown ids out of state.
 */
export function tileProperties(entry: TerrainCell): TerrainProperties {
  if (entry.tile === CUSTOM_TILE) return entry.properties;
  return tileType(entry.tile)?.properties ?? {};
}

/**
 * Why `raw` is not a valid stored {@link TerrainCell}, or `null` when it is. A flag-shaped cell
 * (the pre-v20 format, flags stored directly on the cell) has no `tile` and is named as such.
 */
export function terrainCellProblem(raw: unknown): string | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return "is not a terrain cell object";
  const tile = (raw as { tile?: unknown }).tile;
  if (tile === undefined) return `is flag-format terrain (${JSON.stringify(raw)}), not a tile reference`;
  if (typeof tile !== "string") return `has a non-string tile id (${JSON.stringify(tile)})`;
  if (tile === CUSTOM_TILE) {
    const props = (raw as { properties?: unknown }).properties;
    if (props === null || typeof props !== "object" || Array.isArray(props)) return 'is a "custom" tile with no properties object';
    return null;
  }
  if (!isTileTypeId(tile)) return `names unknown tile "${tile}" (known: ${TILE_TYPE_IDS.join(", ")}, custom)`;
  return null;
}
